import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Alert,
    CircularProgress,
    Divider,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    Person,
    ShoppingCart,
    Payment,
    Print,
    Email,
    LocalShipping,
    CheckCircle,
    Receipt
} from '@mui/icons-material';
import axios from 'axios';

const InOfficePurchase = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [products, setProducts] = useState([]);
    const [recentPurchases, setRecentPurchases] = useState([]);
    const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
    const [currentInvoiceUrl, setCurrentInvoiceUrl] = useState('');
    
    // Form data
    const [formData, setFormData] = useState({
        // Customer info
        customerInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            businessName: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: 'LA',
            zip: ''
        },
        // Product selection
        productId: '',
        selectedOption: 'studyProgramWithBooks',
        quantity: 1,
        // Payment
        paymentMethod: 'cash',
        paymentDetails: {
            reference: '',
            amount: ''
        },
        // Options
        createShippingLabel: false,
        sendEmailInvoice: true,
        notes: ''
    });

    // Order result
    const [orderResult, setOrderResult] = useState(null);

    const steps = ['Customer Information', 'Product Selection', 'Payment & Options', 'Review & Complete'];

    useEffect(() => {
        fetchProducts();
        fetchRecentPurchases();
    }, []);

    const fetchProducts = async () => {
        try {
            console.log('Fetching products...');
            const response = await axios.get('/api/products');
            console.log('Products response:', response.data);
            
            const productsData = response.data?.data?.data || response.data?.data || [];
            console.log('Raw products data:', productsData);
            
            if (Array.isArray(productsData)) {
                const filteredProducts = productsData.filter(p => !p.isHidden && !p.isTrash);
                console.log('Filtered products:', filteredProducts.length);
                setProducts(filteredProducts);
            } else {
                console.error('Products data is not an array:', productsData);
                setProducts([]);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setProducts([]);
        }
    };

    const fetchRecentPurchases = async () => {
        try {
            const response = await axios.get('/api/in-office/recent');
            setRecentPurchases(response.data.purchases || []);
        } catch (err) {
            console.error('Error fetching recent purchases:', err);
        }
    };

    const handleInputChange = (section, field, value) => {
        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const validateStep = (step) => {
        switch (step) {
            case 0: // Customer info
                const { firstName, lastName, email, addressLine1, city, state, zip } = formData.customerInfo;
                return firstName && lastName && email && addressLine1 && city && state && zip;
            case 1: // Product selection
                return formData.productId && formData.selectedOption;
            case 2: // Payment
                return formData.paymentMethod && 
                       (!['cash', 'check'].includes(formData.paymentMethod) || formData.paymentDetails.reference);
            default:
                return true;
        }
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
            setError('');
        } else {
            setError('Please fill in all required fields');
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
        setError('');
    };

    const calculateTotal = () => {
        const product = products.find(p => p._id === formData.productId);
        if (!product) return { subtotal: 0, tax: 0, total: 0 };
        
        const option = product.purchaseOptions?.find(o => o.optionType === formData.selectedOption);
        if (!option) return { subtotal: 0, tax: 0, total: 0 };
        
        const subtotal = option.price * formData.quantity;
        const tax = option.isTaxable ? subtotal * 0.095 : 0; // LA tax rate
        const total = subtotal + tax;
        
        return { subtotal, tax, total };
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await axios.post('/api/in-office/create', formData);
            
            if (response.data.success) {
                setOrderResult(response.data);
                setSuccess(true);
                
                // Print invoice automatically
                if (response.data.invoice?.url) {
                    window.open(response.data.invoice.url, '_blank');
                }
                
                // Reset form for next customer
                setTimeout(() => {
                    resetForm();
                }, 3000);
                
                // Refresh recent purchases
                fetchRecentPurchases();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create purchase');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            customerInfo: {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                businessName: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: 'LA',
                zip: ''
            },
            productId: '',
            selectedOption: 'studyProgramWithBooks',
            quantity: 1,
            paymentMethod: 'cash',
            paymentDetails: {
                reference: '',
                amount: ''
            },
            createShippingLabel: false,
            sendEmailInvoice: true,
            notes: ''
        });
        setActiveStep(0);
        setSuccess(false);
        setOrderResult(null);
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Person /> Customer Information
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                value={formData.customerInfo.firstName}
                                onChange={(e) => handleInputChange('customerInfo', 'firstName', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                value={formData.customerInfo.lastName}
                                onChange={(e) => handleInputChange('customerInfo', 'lastName', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.customerInfo.email}
                                onChange={(e) => handleInputChange('customerInfo', 'email', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={formData.customerInfo.phone}
                                onChange={(e) => handleInputChange('customerInfo', 'phone', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Business Name (Optional)"
                                value={formData.customerInfo.businessName}
                                onChange={(e) => handleInputChange('customerInfo', 'businessName', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Street Address"
                                value={formData.customerInfo.addressLine1}
                                onChange={(e) => handleInputChange('customerInfo', 'addressLine1', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 2 (Optional)"
                                value={formData.customerInfo.addressLine2}
                                onChange={(e) => handleInputChange('customerInfo', 'addressLine2', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                required
                                fullWidth
                                label="City"
                                value={formData.customerInfo.city}
                                onChange={(e) => handleInputChange('customerInfo', 'city', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth required>
                                <InputLabel>State</InputLabel>
                                <Select
                                    value={formData.customerInfo.state}
                                    onChange={(e) => handleInputChange('customerInfo', 'state', e.target.value)}
                                    label="State"
                                >
                                    <MenuItem value="LA">Louisiana</MenuItem>
                                    <MenuItem value="MS">Mississippi</MenuItem>
                                    <MenuItem value="TX">Texas</MenuItem>
                                    <MenuItem value="AL">Alabama</MenuItem>
                                    <MenuItem value="FL">Florida</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                required
                                fullWidth
                                label="ZIP Code"
                                value={formData.customerInfo.zip}
                                onChange={(e) => handleInputChange('customerInfo', 'zip', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                );

            case 1:
                const selectedProduct = products.find(p => p._id === formData.productId);
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <ShoppingCart /> Product Selection
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Select Product</InputLabel>
                                <Select
                                    value={formData.productId}
                                    onChange={(e) => handleInputChange(null, 'productId', e.target.value)}
                                    label="Select Product"
                                >
                                    {products.length === 0 ? (
                                        <MenuItem disabled>Loading products...</MenuItem>
                                    ) : (
                                        products.map(product => (
                                            <MenuItem key={product._id} value={product._id}>
                                                {product.name} - ${product.price}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            {/* Debug info */}
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'gray' }}>
                                Debug: {products.length} products loaded
                            </Typography>
                        </Grid>
                        
                        {selectedProduct && selectedProduct.purchaseOptions && (
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Purchase Option</InputLabel>
                                    <Select
                                        value={formData.selectedOption}
                                        onChange={(e) => handleInputChange(null, 'selectedOption', e.target.value)}
                                        label="Purchase Option"
                                    >
                                        {selectedProduct.purchaseOptions.map(option => (
                                            <MenuItem key={option.optionType} value={option.optionType}>
                                                {option.displayName} - ${option.price}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Quantity"
                                value={formData.quantity}
                                onChange={(e) => handleInputChange(null, 'quantity', parseInt(e.target.value) || 1)}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        
                        {selectedProduct && (
                            <Grid item xs={12}>
                                <Paper elevation={2} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Order Summary
                                    </Typography>
                                    <Typography>Subtotal: ${calculateTotal().subtotal.toFixed(2)}</Typography>
                                    <Typography>Tax: ${calculateTotal().tax.toFixed(2)}</Typography>
                                    <Typography variant="h6" color="primary">
                                        Total: ${calculateTotal().total.toFixed(2)}
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                );

            case 2:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Payment /> Payment & Options
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Payment Method</InputLabel>
                                <Select
                                    value={formData.paymentMethod}
                                    onChange={(e) => handleInputChange(null, 'paymentMethod', e.target.value)}
                                    label="Payment Method"
                                >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="check">Check</MenuItem>
                                    <MenuItem value="credit_card">Credit Card (Manual)</MenuItem>
                                    <MenuItem value="debit_card">Debit Card (Manual)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        {['check', 'credit_card', 'debit_card'].includes(formData.paymentMethod) && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={formData.paymentMethod === 'check' ? 'Check Number' : 'Last 4 Digits'}
                                    value={formData.paymentDetails.reference}
                                    onChange={(e) => handleInputChange('paymentDetails', 'reference', e.target.value)}
                                    required
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notes (Optional)"
                                value={formData.notes}
                                onChange={(e) => handleInputChange(null, 'notes', e.target.value)}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Additional Options
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.sendEmailInvoice}
                                        onChange={(e) => handleInputChange(null, 'sendEmailInvoice', e.target.checked)}
                                    />
                                }
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Email />
                                        Send invoice via email
                                    </Box>
                                }
                            />
                            {formData.selectedOption === 'studyProgramWithBooks' && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.createShippingLabel}
                                            onChange={(e) => handleInputChange(null, 'createShippingLabel', e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <LocalShipping />
                                            Create shipping label (books will be shipped)
                                        </Box>
                                    }
                                />
                            )}
                        </Grid>
                    </Grid>
                );

            case 3:
                const product = products.find(p => p._id === formData.productId);
                const option = product?.purchaseOptions?.find(o => o.optionType === formData.selectedOption);
                const totals = calculateTotal();
                
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                <Receipt /> Review & Complete
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Customer Information
                                </Typography>
                                <Typography>{formData.customerInfo.firstName} {formData.customerInfo.lastName}</Typography>
                                {formData.customerInfo.businessName && (
                                    <Typography>{formData.customerInfo.businessName}</Typography>
                                )}
                                <Typography>{formData.customerInfo.email}</Typography>
                                <Typography>{formData.customerInfo.phone}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography>{formData.customerInfo.addressLine1}</Typography>
                                {formData.customerInfo.addressLine2 && (
                                    <Typography>{formData.customerInfo.addressLine2}</Typography>
                                )}
                                <Typography>
                                    {formData.customerInfo.city}, {formData.customerInfo.state} {formData.customerInfo.zip}
                                </Typography>
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                    Order Details
                                </Typography>
                                <Typography>Product: {product?.name}</Typography>
                                <Typography>Option: {option?.displayName}</Typography>
                                <Typography>Quantity: {formData.quantity}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography>Subtotal: ${totals.subtotal.toFixed(2)}</Typography>
                                <Typography>Tax: ${totals.tax.toFixed(2)}</Typography>
                                <Typography variant="h6" color="primary">
                                    Total: ${totals.total.toFixed(2)}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography>Payment: {formData.paymentMethod.replace('_', ' ').toUpperCase()}</Typography>
                                {formData.paymentDetails.reference && (
                                    <Typography>Reference: {formData.paymentDetails.reference}</Typography>
                                )}
                            </Paper>
                        </Grid>
                        
                        {(formData.sendEmailInvoice || formData.createShippingLabel) && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    <Typography variant="subtitle2" fontWeight="bold">Actions to be performed:</Typography>
                                    {formData.sendEmailInvoice && <Typography>• Invoice will be emailed to customer</Typography>}
                                    {formData.createShippingLabel && <Typography>• Shipping label will be created in ShipStation</Typography>}
                                    <Typography>• Invoice will open for printing</Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                In-Office Purchase
            </Typography>
            
            {success && orderResult && (
                <Alert 
                    severity="success" 
                    onClose={() => setSuccess(false)}
                    sx={{ mb: 3 }}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        Purchase completed successfully!
                    </Typography>
                    <Typography>Order Number: {orderResult.order.orderNumber}</Typography>
                    {orderResult.user.isNew && (
                        <Typography>
                            New account created for {orderResult.user.email} 
                            (Temporary password: {orderResult.user.tempPassword})
                        </Typography>
                    )}
                    {orderResult.shippingLabel && (
                        <Typography>
                            Tracking Number: {orderResult.shippingLabel.trackingNumber}
                        </Typography>
                    )}
                </Alert>
            )}
            
            {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            
            <Card>
                <CardContent>
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    
                    {renderStepContent(activeStep)}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        
                        {activeStep === steps.length - 1 ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                            >
                                Complete Purchase
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
            
            {/* Recent Purchases Section */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Recent In-Office Purchases
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentPurchases.map((purchase) => (
                                    <TableRow key={purchase._id}>
                                        <TableCell>{purchase.orderNumber}</TableCell>
                                        <TableCell>
                                            {purchase.user?.firstName} {purchase.user?.lastName}
                                            <br />
                                            <Typography variant="caption">{purchase.user?.email}</Typography>
                                        </TableCell>
                                        <TableCell>{new Date(purchase.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>${purchase.total?.toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={purchase.status} 
                                                color="success" 
                                                size="small" 
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                startIcon={<Print />}
                                                onClick={() => window.open(`/api/in-office/invoice/${purchase._id}`, '_blank')}
                                            >
                                                Invoice
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default InOfficePurchase;